import { ChangeEventHandler } from 'react';
import { Button, TERTIARY, EXTRA_SMALL } from '@glu/buttons-react';
import { Input, CheckboxRadio } from '@glu/form-components';
import locale from '@glu/locale';
import ColumnSortableList from '../ColumnSortableList/ColumnSortableList';
import useStyles from './Columns.styles';
import ApplyCancelButtons from '../../ApplyCancelButtons/ApplyCancelButtons';

export interface IColumnState {
  [key: string]: unknown;
  /**
   * The id of the column. Necessary because @dnd-kit doesn't allow you to customize this variable
   * for a sortable list. It expects the array has an id property
   * */
  id: string;
  /** The id of the column. Sometimes referred to as the field property */
  colId: string;
  /** The display name of the column */
  headerName: string;
  /** Whether the column is locked or not */
  lockPosition?: boolean | 'left' | 'right';
  /** Whether the column is pinned or not */
  pinned?: boolean | 'left' | 'right' | null;
  /** Whether the column is a primary column or not (only used in mobile grid) */
  primary?: boolean;
  /** Whether or not the column is hidden */
  hide?: boolean | null;
}

export interface IColumnsProps {
  /**
   * Whether or not to apply changes automatically without having to press the apply button.
   * Hides the apply and cancel button if true
   */
  autoApplyChanges?: boolean;
  /** Disables column dragging */
  disableDrag?: boolean;
  /** Disables column pinning */
  disablePinning?: boolean;
  /**
   * The column state of the grid or whatever you're trying to control. The column should be an
   * array with at least the property of id (id is required by dnd-kit otherwise the
   * dragging/dropping) won't work.
   */
  columns: IColumnState[];
  /** The value of the filter input */
  filterValue: string;
  /** Maximum number of columns you can put the primary area */
  maxPrimary?: number;
  /** Callback used when the apply button is pressed  */
  onApply?(): void;
  /** Callback the cancel button is pressed */
  onCancel?(): void;
  /** Callback for when the filter input is changed */
  onFilterChange: ChangeEventHandler<HTMLInputElement>;
  /** Callback used when an individual column's hide checkbox is toggled */
  onShow?(id: IColumnState['colId']): void;
  /** Callback used when the show all checkbox is toggled */
  onShowAll?(): void;
  /**
   * Callback used when a column is moved from primary to other columns or vice-versa while dragging
   * passed back the id of the column that is being dragged
   */
  onMove?(id: IColumnState['colId']): void;
  /**
   * Callback used when a column is dropped after being dragged. Passed back the new
   * state of the columns in order.
   */
  onMoveEnd?(newColDefs: IColumnState[]): void;
  /**
   * Callback used when the column is pinned.
   * Passes the id of the column and the new pinned state
   */
  onPin?(id: IColumnState['colId'], pinned: IColumnState['pinned']): void;
  /** Callback used when the button for unpin all is clicked */
  onUnpinAll?(): void;
  /** Whether or not the checkbox for showAll is checked or not */
  showAll: boolean;
  /** Whether or not to show the unpin all button */
  showUnpinAll: boolean;
}

const noop = () => null;

const Columns = ({
  autoApplyChanges,
  columns,
  disableDrag,
  disablePinning,
  filterValue,
  maxPrimary,
  onApply = noop,
  onCancel = noop,
  onFilterChange,
  onMove = noop,
  onMoveEnd = noop,
  onPin = noop,
  onShow = noop,
  onShowAll = noop,
  onUnpinAll = noop,
  showAll,
  showUnpinAll,
}: IColumnsProps) => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.columnActions}>
        <Input
          disableInputAPI
          labelText={locale.get('dataComponents.column.search')}
          name="findColumnByName"
          onChange={onFilterChange}
          value={filterValue}
        />
        <span>{locale.get('dataComponents.columns')}</span>
        {showUnpinAll && (
          <Button
            className={classes.unpinAllButton}
            onClick={onUnpinAll}
            size={EXTRA_SMALL}
            variant={TERTIARY}
          >
            {locale.get('dataComponents.unpinAllColumns')}
          </Button>
        )}
      </div>
      <div className={classes.contentScroll}>
        <div className={classes.wrapper}>
          <div className={classes.itemWrapper}>
            <CheckboxRadio
              checked={showAll}
              data-qa="switch-show-all"
              labelText={locale.get('dataComponents.selectAll')}
              name="showAll"
              onChange={onShowAll}
            />
          </div>
          <ColumnSortableList
            columnStates={columns}
            disableDrag={disableDrag}
            disablePinning={disablePinning}
            maxPrimary={maxPrimary}
            onMove={onMove}
            onMoveEnd={onMoveEnd}
            onPin={onPin}
            onShow={onShow}
          />
        </div>
      </div>
      {autoApplyChanges ? null : <ApplyCancelButtons onApply={onApply} onCancel={onCancel} />}
    </>
  );
};

export default Columns;
